import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import { SURFACE } from "../common/designTokens";

const compact = "@media " + COMPACT_QUERY;

const NOTE_MAX_LENGTH = 500;
const DEFAULT_HINT = "ระบุหมายเหตุ (ไม่บังคับ)";
const DEFAULT_PLACEHOLDER = "กรอกหมายเหตุ";

const CONTEXT_BOX_SX = { bgcolor: SURFACE.line2, borderRadius: 1, px: 1.5, py: 1, mb: 2 } as const;
const CONTEXT_TITLE_SX = { fontWeight: 600 } as const;

/** What a note is about: a bold first line and a secondary second one. */
export interface NoteContext {
  title: string;
  detail: string;
}

interface DepositNoteModalProps {
  open: boolean;
  note: string | null | undefined;
  loading?: boolean;
  /** Shown in a grey box at the top, so the note is written against the record it explains. */
  context?: NoteContext;
  /** What to write; the dialog adds the length limit after it. */
  hint?: string;
  placeholder?: string;
  onClose: () => void;
  onSave: (note: string) => void;
}

/**
 * หมายเหตุ modal (Reference 5). With an existing note the dialog opens in a
 * read-only view (note text + แก้ไข); no existing note opens straight into an
 * editable textarea. Saving emits the trimmed value to the caller.
 */
export default function DepositNoteModal({
  open,
  note,
  loading,
  context,
  hint = DEFAULT_HINT,
  placeholder = DEFAULT_PLACEHOLDER,
  onClose,
  onSave,
}: DepositNoteModalProps) {
  const hasNote = Boolean(note && note.trim());
  const [editing, setEditing] = useState<boolean>(!hasNote);
  const [text, setText] = useState<string>(note ?? "");

  useEffect(() => {
    if (open) {
      setText(note ?? "");
      setEditing(!hasNote);
    }
  }, [open, note, hasNote]);

  const handleSave = () => onSave(text.trim());

  const handleCancelEdit = () => {
    // Revert to the view of the saved note when one exists; otherwise close.
    if (hasNote) {
      setText(note ?? "");
      setEditing(false);
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { sx: { [compact]: { maxHeight: "94vh" } } },
      }}
    >
      <DialogTitle>หมายเหตุ</DialogTitle>
      <DialogContent sx={{ [compact]: { overflowY: "auto" } }}>
        {context && (
          <Box sx={CONTEXT_BOX_SX}>
            <Typography variant="body2" sx={CONTEXT_TITLE_SX}>
              {context.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {context.detail}
            </Typography>
          </Box>
        )}
        {editing ? (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              {hint} สูงสุด {NOTE_MAX_LENGTH} ตัวอักษร
            </DialogContentText>
            <TextField
              fullWidth
              multiline
              rows={3}
              autoFocus
              placeholder={placeholder}
              value={text}
              disabled={loading}
              onChange={(e) =>
                setText(e.target.value.slice(0, NOTE_MAX_LENGTH))
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "right", mt: 0.5 }}
            >
              {text.length}/{NOTE_MAX_LENGTH}
            </Typography>
          </>
        ) : (
          <DialogContentText
            sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
          >
            {note}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button disabled={loading} onClick={handleCancelEdit}>
              ยกเลิก
            </Button>
            <Button loading={loading} variant="contained" onClick={handleSave}>
              บันทึก
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>ปิด</Button>
            <Button variant="contained" onClick={() => setEditing(true)}>
              แก้ไข
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
